import { useMemo } from 'react';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useTranslation } from '@frontend/i18n';
import { BreadcrumbPathProps } from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../bulk-message-editor.context';
import { NEW_CAMPAIGN_ID } from '../constants';
import { useBulkNavigator } from './use-bulk-navigator';
import { useBulkUrlData } from './use-bulk-url-data';

export const useBulkBreadcrumbs = () => {
  const { t } = useTranslation('bulk-messaging');
  const { campaignId, seedCampaignId, subNav } = useBulkUrlData();
  const { campaignType } = useBulkMessageEditorContext();
  const navigateTo = useBulkNavigator(campaignType);

  return useMemo(() => {
    // BASE BREADCRUMB
    const baseType = campaignType === CampaignType.SMS ? 'text' : 'email';
    const base: BreadcrumbPathProps[] = [
      {
        label: campaignType === CampaignType.SMS ? t('Text Campaigns') : t('Email Campaigns'),
        // TODO: Figure out why the following causes a "Maximum update depth exceeded" bug.
        // navigateTo.campaigns() causes the bug, navigateTo.drafts() does not.
        // Also the app successfully changes routes and loads the Campaigns component,
        // but crashes soon after. At this point the campaign editor, its context, and everything here has unmounted.
        // Using the to prop instead of onClick does not cause the bug.
        // onClick: () => (subNav === 'drafts' ? navigateTo.drafts() : navigateTo.campaigns()),
        to: subNav === 'drafts' ? '/bulk/drafts' : `/bulk/all-campaigns/${baseType}`,
      },
    ];

    // STEPPER BREADCRUMB
    const stepperLabel =
      campaignId === NEW_CAMPAIGN_ID
        ? seedCampaignId
          ? t('Duplicate Campaign')
          : t('New Campaign')
        : t('Edit Campaign');
    const onStepperClick = () => {
      navigateTo.editor({ campaignId: campaignId ?? NEW_CAMPAIGN_ID, seedCampaignId });
    };
    const stepper: BreadcrumbPathProps[] = [...base, { label: stepperLabel }];

    // NEW EMAIL BREADCRUMB
    const newEmailLabel = campaignType === CampaignType.SMS ? t('Compose Text') : t('Compose Email');
    const onNewEmailClick = () => {
      navigateTo.new({ campaignId: campaignId ?? NEW_CAMPAIGN_ID, seedCampaignId });
    };
    const newEmail: BreadcrumbPathProps[] = [
      ...base,
      { label: stepperLabel, onClick: onStepperClick },
      { label: newEmailLabel },
    ];

    // TEMPLATES BREADCRUMB
    const templatesLabel = t('Select Template');
    const templates: BreadcrumbPathProps[] = [
      ...base,
      { label: stepperLabel, onClick: onStepperClick },
      { label: newEmailLabel, onClick: onNewEmailClick },
      { label: templatesLabel },
    ];

    return {
      stepper,
      newEmail,
      templates,
    };
  }, [campaignId, campaignType, navigateTo, seedCampaignId, subNav, t]);
};
